import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PaginationType, TaxFiltersType, TaxType } from './types.ts'
import {Button} from "@/components/ui/button.tsx";
import {DollarSign, Edit, Plus, Search, Trash2} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import {Input} from "@/components/ui/input.tsx";
import Pagination from "@/components/pagination.tsx";

interface IndexProps {
  taxes: TaxType[]
  filters: TaxFiltersType
  pagination: PaginationType
  can_create: boolean
  can_destroy?: boolean
  stats: {
    total_taxes: number
    active_taxes: number
    total_collected: number
  }
}

export default function Index({ taxes, filters, pagination, can_create, can_destroy = false, stats }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)
  const formatRuleType = (ruleType?: string) => {
    if (!ruleType) return "Non defini"
    if (ruleType === "flat_rate") return "Montant fixe"
    if (ruleType === "per_item") return "Par nombre d'elements"
    if (ruleType === "percentage") return "Pourcentage"
    return ruleType
  }

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/taxes", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (tax: TaxType) => {
    if (!window.confirm(`Supprimer la taxe "${tax.name}" ?`)) return

    router.delete(`/admin/taxes/${tax.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Taxes" />
      <div className="p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Les taxes</h2>
            <p className="text-muted-foreground">Configurer les types, montants et regles de collecte</p>
          </div>
          {can_create && (
            <Link href="/admin/taxes/new">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Ajouter une taxe
              </Button>
            </Link>
          )}
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nombre total de taxes</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total_taxes ?? 0}</p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Taxes actives</p>
                <p className="text-2xl font-bold text-foreground">{stats?.active_taxes ?? 0}</p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <DollarSign className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Montant total collecte</p>
                <p className="text-xl font-bold text-foreground">{(stats?.total_collected ?? 0).toLocaleString()} FCFA</p>
              </div>
            </div>
          </Card>
        </div>


        <Card className="border-border bg-card p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                  placeholder="Rechercher une taxe par nom, code ou description..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-3">
            {taxes.map((tax) => (
                <div
                  key={tax.id}
                  className="cursor-pointer rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50"
                  role="button"
                  tabIndex={0}
                  onClick={() => router.visit(`/admin/taxes/${tax.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      router.visit(`/admin/taxes/${tax.id}`)
                    }
                  }}
                >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-semibold text-foreground">{tax.name}</p>
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
                        {tax.code}
                      </span>
                          {tax.active && (
                              <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                          Actif
                        </span>
                          )}
                        </div>

                        <p className="mt-2 text-sm text-muted-foreground">0</p>

                        <div className="mt-3 grid gap-4 md:grid-cols-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Montant (regle active)</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">
                              {(tax.active_tax_rule_amount ?? 0).toLocaleString()} FCFA
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Contribuables payeurs</p>
                            <p className="mt-1 text-sm font-medium text-foreground">
                              {tax.total_taxpayers_paying ?? 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Montant collecte</p>
                            <p className="mt-1 text-sm font-semibold text-success">
                              {(tax.total_collected_all_time ?? 0).toLocaleString()} FCFA
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Type de regle</p>
                            <p className="mt-1 text-sm font-medium text-foreground">
                              {formatRuleType(tax.active_tax_rule_type)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="gap-2" aria-label={`Voir ${tax.name}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {can_destroy && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            aria-label={`Supprimer ${tax.name}`}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              handleDestroy(tax)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
            ))}
          </div>
        </Card>

        <div className="mt-6">
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            basePath="/admin/taxes"
            query={query}
          />
        </div>
      </div>
    </>
  )
}
